// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * ARCHAT — Bootstrap y entrada de la aplicación
 * Orden: diagnósticos → Sentry → audit/self-check → React (ErrorBoundary → App)
 * App se carga con dynamic import para evitar TDZ/ciclos (AuthContext ↔ apiService ↔ bootstrap).
 * Nunca deja pantalla en blanco: fallback si falla bootstrap o render.
 */

import { APP_VERSION, BUILD_TIMESTAMP } from './version.js';

if (typeof console !== 'undefined' && typeof console.log === 'function') {
  console.log('[APP_VERSION]', APP_VERSION, BUILD_TIMESTAMP);
}

// === SOCKET FORENSIC INTERCEPTOR START ===
import { io as originalIo } from "socket.io-client";

window.__ARCHAT_SOCKET_CREATIONS__ = [];

function forensicIo(...args) {
  const stack = new Error().stack;

  console.warn('[FORENSIC_SOCKET_CREATION]', {
    args,
    stack
  });

  window.__ARCHAT_SOCKET_CREATIONS__.push({
    args,
    stack,
    timestamp: Date.now()
  });

  return originalIo(...args);
}

// override global
window.io = forensicIo;
// === SOCKET FORENSIC INTERCEPTOR END ===

import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './styles/global.css';
import './styles/foldables.css';
import './styles/notifications.css';
import ErrorBoundary from './components/ErrorBoundary';
import ReactSafeBoundary from './components/System/ReactSafeBoundary';
import { request as apiClientRequest } from './services/apiClient';
import { initSentry } from './utils/sentryInit';
import { errorBus } from './core/errors/errorBus';
import { loadTranslations } from './utils/translations/index.js';
import { validateTranslations } from './i18n/validateTranslations';
import { SUPPORTED_LANGUAGES } from './i18n/supportedLanguages.js';
import { registerPush } from './pwa/push';
import { requestNotificationPermission } from './utils/NotificationPermission';
import './pwa/installPrompt'; /* Registrar beforeinstallprompt temprano */
import { getUnreadCount } from './api/unread';
import { updateBadge } from './pwa/badge';
import { applyTheme } from './hooks/useTheme';
import { STORAGE_KEYS, BASE_PATH } from './utils/constants';
import { devLog } from './utils/devConsoleLog';
import { BUILD_ID as frontendBuildId } from './config/buildInfo';

/** Duplicación basename + ruta interna (p. ej. /app + /app) → normalizar a una sola raíz SPA. Antes de cualquier otro bootstrap. */
if (typeof window !== 'undefined') {
  let pathname = window.location.pathname || '';
  const spaRoot = (BASE_PATH || '/app').replace(/\/$/, '');
  const doubleSegment = spaRoot && spaRoot !== '/' ? `${spaRoot}/${spaRoot.replace(/^\//, '')}` : '';
  if (doubleSegment && pathname.startsWith(doubleSegment)) {
    console.warn('[DOUBLE_APP_DETECTED]', pathname);
    const esc = doubleSegment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`^${esc}`);
    while (pathname.startsWith(doubleSegment)) {
      pathname = pathname.replace(re, spaRoot);
    }
    window.location.replace(
      `${pathname}${window.location.search || ''}${window.location.hash || ''}`
    );
  }
}

/** Evita quedarse en prefijo legacy /archat (404) antes de cualquier fetch/React. */
if (typeof window !== 'undefined') {
  const p = window.location.pathname || '';
  if (p === '/archat' || p.startsWith('/archat/')) {
    const suffix = p.replace(/^\/archat\/?/, '');
    const spaRoot = (BASE_PATH || '/app').replace(/\/$/, '');
    const nextPath = suffix ? `${spaRoot}/${suffix.replace(/^\//, '')}` : spaRoot;
    const target = `${nextPath}${window.location.search || ''}${window.location.hash || ''}`;
    console.warn(
      '[ARCHAT_ROUTE_SOURCE_FOUND] pathname legacy /archat (bundle viejo, bookmark, SW o HTML estático); destino:',
      target
    );
    console.warn('[FORCE_ROUTE_FIX] redirecting from /archat to', spaRoot);
    window.location.replace(target);
  }
}

/** Raíz API para telemetría (log.php); no usar el prefijo SPA (/app) para no generar /app/api/... */
const RESOLVED_API_ROOT = (
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE) || '/api'
).replace(/\/$/, '');

if (typeof window !== 'undefined') {
  window.__ARCHAT_BUILD_ID__ = frontendBuildId;
  window.__ARCHAT_THEME_OVERRIDE_GUARD__ = 'THEME_OVERRIDE_BLOCKED';
  const apiBase = (import.meta.env?.VITE_API_BASE || '/api').replace(/\/?$/, '');
  const healthUrl = `${window.location.origin}${apiBase.startsWith('/') ? apiBase : `/${apiBase}`}/health.php`;
  void fetch(healthUrl, { credentials: 'include' })
    .then((res) => (res.ok ? res.json() : {}))
    .then((data) => {
      devLog('[BUILD CHECK]', {
        frontend: frontendBuildId,
        backend: data?.build_id != null ? String(data.build_id) : '(n/a)',
      });
    })
    .catch(() => {
      devLog('[BUILD CHECK]', {
        frontend: frontendBuildId,
        backend: '(unavailable)',
      });
    });
} else {
  devLog('[BUILD CHECK]', { frontend: frontendBuildId, backend: '(no window)' });
}

function setDiagnostics(translationsRegistry = {}) {
  try {
    if (typeof window !== 'undefined') {
      window.ARCHAT_DIAG = {
        version: import.meta.env.VITE_BUILD_TOKEN || 'dev',
        build: import.meta.env.MODE || 'development',
        buildId: frontendBuildId,
        baseUrl: import.meta.env.VITE_PUBLIC_BASE || (BASE_PATH.endsWith('/') ? BASE_PATH : `${BASE_PATH}/`),
        apiBase: import.meta.env.VITE_API_BASE || '/api',
        ts: new Date().toISOString()
      };
      window.__ARCHAT_TRANSLATIONS_REGISTRY = translationsRegistry;
    }
  } catch (_) {}
  try {
    if (translationsRegistry?.en && translationsRegistry?.es) {
      validateTranslations(translationsRegistry.en, translationsRegistry.es);
    }
  } catch (_) {}
}

/**
 * Build-id en localStorage para diagnóstico. Si cambia el build, marcar teardown del SW antes del
 * registro en `load` (evita bundle/HTML viejo + SW que interceptaba fetch cross-origin).
 */
function invalidateServiceWorkerIfNewBuild() {
  try {
    if (typeof localStorage === 'undefined') return;
    const prev = localStorage.getItem('archat_build_id');
    const next = String(frontendBuildId);
    if (prev && prev !== next && typeof window !== 'undefined') {
      window.__ARCHAT_SW_NEEDS_HARD_INVALIDATE__ = true;
      try {
        console.warn('[SW_BUILD_CHANGED]', { prev, next });
      } catch (_) {}
    }
    localStorage.setItem('archat_build_id', next);
  } catch (_) {}
}

// ===============================
// SERVICE WORKER SAFE REGISTER
// ===============================
// SW solo bajo BASE_PATH (/app/); nunca scope "/" ni registro fuera de la SPA.
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  const swRoot = (BASE_PATH || '/app').replace(/\/$/, '');
  const expectedScopePath = `${swRoot}/`;
  const officialScriptPath = `${swRoot}/service-worker.js`;
  const legacyScriptPath = `${swRoot}/sw.js`;

  const resolveRegistrationScriptUrl = (reg) =>
    reg?.active?.scriptURL
    || reg?.waiting?.scriptURL
    || reg?.installing?.scriptURL
    || '';

  const cleanupLegacyServiceWorkers = () =>
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => {
        regs.forEach((reg) => {
          try {
            const scopeUrl = new URL(reg.scope);
            const scopePath = scopeUrl.pathname || '/';
            const scriptUrl = resolveRegistrationScriptUrl(reg);
            const isLegacyRoot = scopePath === '/';
            const isOutsideApp = !scopePath.startsWith(expectedScopePath);
            const isLegacyScript = scriptUrl.includes(legacyScriptPath);
            if (import.meta.env.DEV || isLegacyRoot || isOutsideApp || isLegacyScript) {
              reg.unregister();
              console.warn('[SW_UNREGISTER]', { scope: reg.scope, script: scriptUrl || '(unknown)' });
            }
          } catch (_) {
            reg.unregister();
          }
        });
      })
      .catch(() => {});

  if (import.meta.env.DEV) {
    cleanupLegacyServiceWorkers();
  }

  if (import.meta.env.PROD) {
    window.addEventListener('load', () => {
      void (async () => {
        try {
          if (typeof window !== 'undefined' && window.__ARCHAT_SW_NEEDS_HARD_INVALIDATE__) {
            window.__ARCHAT_SW_NEEDS_HARD_INVALIDATE__ = false;
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
            if ('caches' in window) {
              const keys = await caches.keys();
              await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
            }
            try {
              console.warn('[SW_HARD_INVALIDATE_ON_NEW_BUILD]');
            } catch (_) {}
          }
        } catch (_) {}
        try {
          await cleanupLegacyServiceWorkers();
        } catch (_) {}
        try {
          const reg = await navigator.serviceWorker.register(officialScriptPath, {
            scope: expectedScopePath,
          });
          console.log('[SW_REGISTER_OK]', reg.scope);
          try {
            const origin = window.location.origin || '';
            console.info('[PWA_MANIFEST_PATH]', `${origin}${swRoot}/manifest.json`);
            console.info('[PWA_SCOPE_EFFECTIVE]', expectedScopePath);
            console.info('[PWA_SW_REGISTER]', `${origin}${officialScriptPath}`);
            console.info('[PWA_SW_SCOPE]', reg.scope);
          } catch (_) {}
        } catch (err) {
          console.error('[SW_REGISTER_ERROR]', err);
        }
      })();
    });
  }
}

function setGlobalErrorHandlers() {
  if (typeof window === 'undefined') return;
  try {
    window.onerror = function (msg, url, line, col, error) {
      try {
        const err = error || new Error(String(msg));
        console.error('[FULL_ERROR]', err);
        console.error('[STACK]', err?.stack);
      } catch (_) {}
      if (window.__SENTRY__ && typeof window.__SENTRY__.captureException === 'function') {
        try { window.__SENTRY__.captureException(error || new Error(String(msg))); } catch (_) {}
      }
      try {
        errorBus.emit({
          code: 'E-UI-UNCAUGHT',
          message: (error && error.message) || String(msg),
          path: window.location.pathname,
          buildId: frontendBuildId,
          timestamp: new Date().toISOString(),
        });
      } catch (_) {}
      try {
        const body = JSON.stringify({
          level: 'error',
          message: 'Global JS Error: ' + msg,
          context: { url, line, col, error: (error && error.toString()) || null, stack: (error && error.stack) || null, event: 'GLOBAL_JS_ERROR' }
        });
        const apiPrefix = RESOLVED_API_ROOT.startsWith('/') ? RESOLVED_API_ROOT : `/${RESOLVED_API_ROOT}`;
        const logUrl = `${window.location.origin}${apiPrefix}/log.php`;
        apiClientRequest(logUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body }).catch(() => {});
      } catch (_) {}
      return false;
    };
    window.onunhandledrejection = function (e) {
      try {
        const r = e?.reason;
        console.error('[FULL_ERROR]', r);
        console.error('[STACK]', r?.stack);
      } catch (_) {}
      if (window.__SENTRY__ && typeof window.__SENTRY__.captureException === 'function') {
        try { window.__SENTRY__.captureException(e.reason); } catch (_) {}
      }
      try {
        errorBus.emit({
          code: 'E-UI-UNCAUGHT',
          message: (e.reason && (e.reason.message || (typeof e.reason.toString === 'function' ? e.reason.toString() : String(e.reason)))) || String(e.reason || 'Unknown'),
          path: window.location.pathname,
          buildId: frontendBuildId,
          timestamp: new Date().toISOString(),
        });
      } catch (_) {}
      try {
        const body = JSON.stringify({
          level: 'error',
          message: 'Unhandled Rejection: ' + (e.reason?.toString() || 'Unknown'),
          context: { reason: (e.reason && e.reason.toString()) || null, stack: (e.reason && e.reason.stack) || null, event: 'UNHANDLED_PROMISE_REJECTION' }
        });
        const apiPrefix = RESOLVED_API_ROOT.startsWith('/') ? RESOLVED_API_ROOT : `/${RESOLVED_API_ROOT}`;
        const logUrl = `${window.location.origin}${apiPrefix}/log.php`;
        apiClientRequest(logUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body }).catch(() => {});
      } catch (_) {}
    };
  } catch (_) {}
}

function showBootstrapFailure(reason) {
  try {
    const root = document.getElementById('root');
    if (!root) return;
    root.innerHTML = [
      '<div style="position:fixed;inset:0;background:#1a1a1a;color:var(--color-text-primary);display:flex;flex-direction:column;align-items:center;justify-content:center;padding:24px;font-family:system-ui,sans-serif;text-align:center;">',
      '<h1 style="font-size:1.5rem;margin-bottom:1rem;">ArChat® — Error de arranque</h1>',
      '<p style="margin-bottom:1rem;max-width:480px;">' + (reason || 'La aplicación no pudo iniciar.') + '</p>',
      '<button onclick="window.location.reload()" style="padding:10px 20px;font-size:1rem;cursor:pointer;background:#fff;color:#000;border:none;border-radius:4px;">Recargar</button>',
      '</div>'
    ].join('');
  } catch (_) {}
}

/** Aplicar theme desde localStorage ANTES de montar React. archat_theme es la única fuente; fallback argentina. */
function applyThemeBeforeMount() {
  if (typeof document === 'undefined' || typeof localStorage === 'undefined') return;
  const savedTheme = localStorage.getItem('archat_theme');
  const theme = (savedTheme && (savedTheme === 'dark' || savedTheme === 'argentina')) ? savedTheme : 'argentina';
  applyTheme(theme);
  devLog('[BOOTSTRAP_THEME]', savedTheme ?? '(none → argentina)');
}

/** Boot hook: si la URL tiene ?lang= y es un idioma soportado, guardar en localStorage para que la UI arranque en ese idioma (p. ej. link de invitación). */
function applyLanguageFromQueryBeforeMount() {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const qLang = params.get('lang');
    if (!qLang || typeof qLang !== 'string') return;
    const lang = qLang.trim().toLowerCase().replace(/[^a-z\-]/g, '');
    const supported = SUPPORTED_LANGUAGES;
    const base = lang.length >= 2 ? lang.slice(0, 2) : lang;
    if (supported.includes(base) || supported.includes(lang)) {
      const toStore = supported.includes(lang) ? lang : base;
      localStorage.setItem('archat_language', toStore);
    }
  } catch (_) {}
}

/** Diagnóstico visual: ?diagnostic=1 o localStorage archat_diagnostic=1 → muestra banner para confirmar app ArChat y CSS cargado. */
function applyDiagnosticBanner() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('diagnostic') === '1';
  const fromStorage = localStorage.getItem('archat_diagnostic') === '1';
  if (!fromUrl && !fromStorage) return;
  document.body.classList.add('archat-diagnostic');
  const theme = document.documentElement.getAttribute('data-theme') || '(no set)';
  const buildId = typeof window.__ARCHAT_BUILD_ID__ !== 'undefined' ? window.__ARCHAT_BUILD_ID__ : frontendBuildId;
  const msg = `DIAG: ArChat | theme=${theme} | build=${buildId} | path=${window.location.pathname} | Si ves esto = app ArChat + JS. Barra verde abajo = global.css.`;
  const div = document.createElement('div');
  div.setAttribute('id', 'archat-diagnostic-banner');
  div.style.cssText = 'position:fixed;top:0;left:0;right:0;background:#2563eb;color:#fff;padding:10px 16px;font-size:13px;z-index:999999;text-align:center;font-family:system-ui,sans-serif;';
  div.textContent = msg;
  document.body.appendChild(div);
  devLog('[ARCHAT_DIAG]', msg);
}

async function bootstrap() {
  applyThemeBeforeMount();
  applyLanguageFromQueryBeforeMount();
  applyDiagnosticBanner();
  invalidateServiceWorkerIfNewBuild();
  setGlobalErrorHandlers();
  try { initSentry(); } catch (_) {}
  // Iniciar runtime monitor frontend (buffer eventos + debug endpoint)
  try {
    const { initRuntimeMonitor } = await import('./utils/runtimeMonitor');
    initRuntimeMonitor();
  } catch (_) {}
  // Phase 3: Cargar es y en dinámicamente antes de diagnostics y mount
  let loadedTranslations = {};
  try {
    const [es, en] = await Promise.all([loadTranslations('es'), loadTranslations('en')]);
    loadedTranslations = { es, en };
  } catch (e) {
    console.warn('[BOOTSTRAP] Error preloading translations:', e?.message);
  }
  setDiagnostics(loadedTranslations);
  // Audit y self-check en chunks dinámicos para evitar ciclos de dependencia (TDZ: LoginView → AuthContext/LanguageContext)
  import('./utils/SystemAudit').then((m) => { try { m.runSystemAudit(); } catch (_) {} }).catch(() => {});
  import('./utils/systemSelfCheck').then((m) => { try { m.runSystemSelfCheck(); } catch (_) {} }).catch(() => {});
}

function mountApp() {
  const rootEl = document.getElementById('root');
  if (!rootEl) {
    showBootstrapFailure('Elemento #root no encontrado.');
    return;
  }
  try {
    const root = ReactDOM.createRoot(rootEl);
    // Placeholder mientras se carga el chunk de App (evita TDZ por ciclo main → App → AuthContext → apiService)
    root.render(
      <div style={{ position: 'fixed', inset: 0, background: '#1a1a1a', color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui' }}>
        Cargando…
      </div>
    );
    import('./App').then((m) => {
      const App = m.default;
      root.render(
        <React.StrictMode>
          <ErrorBoundary>
            <ReactSafeBoundary>
              <App />
            </ReactSafeBoundary>
          </ErrorBoundary>
        </React.StrictMode>
      );
    }).catch((e) => {
      showBootstrapFailure(e && (e.message || String(e)));
      if (typeof window !== 'undefined' && window.__SENTRY__ && typeof window.__SENTRY__.captureException === 'function') {
        try { window.__SENTRY__.captureException(e); } catch (_) {}
      }
    });
  } catch (e) {
    showBootstrapFailure(e && (e.message || String(e)));
    if (typeof window !== 'undefined' && window.__SENTRY__ && typeof window.__SENTRY__.captureException === 'function') {
      try { window.__SENTRY__.captureException(e); } catch (_) {}
    }
  }
}

bootstrap();
mountApp();

async function checkForUpdate() {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return;
  reg.update();
}

async function refreshUnread() {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
    if (!token || !userId) return;
    const count = await getUnreadCount();
    updateBadge(count);
  } catch (e) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('Unread badge error', e);
    }
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('focus', checkForUpdate);
  window.addEventListener('focus', refreshUnread);
  window.addEventListener('load', async () => {
    await requestNotificationPermission();
    await registerPush();
    refreshUnread();
  });
}
